import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  Tab,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import Div from "@jumbo/shared/Div/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { Field, useFormikContext } from "formik";
import { Label } from "app/pages/presentation/module/form/types";
import {
  DSOK_ORDER_MANAGEMENT_TYPES,
  DSOK_PRODUCTS_MANAGEMENT_TYPES,
} from "app/utils/constants/settings";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import { useSnackbar } from "notistack";
import {
  fetchDSOKProducts,
  getDotykackaBranch,
} from "app/services/api/management";
import {
  makeDotykackaRefreshToken,
  testWooConnection,
  importWooProducts,
  getWooSyncStatus,
  syncOberonViaDla,
} from "app/services/api/integrations";
import { TabContext, TabList, TabPanel } from "@mui/lab";

const FIELD_NAME = "INTEGRATIONS_FIELD";

/** Liquid template for Shopify order confirmation email (informational only; not saved). */
const SHOPIFY_TICKET_URL_TEMPLATE_PATH =
  "/dsok/processed-order?s={{ order_status_url | url_encode }}";

/** Sample processed-order page opened from "Ukážka" (informational only; not saved). */
const PROCESSED_ORDER_PREVIEW_URL =
  "https://dsok.datavision-online.eu/dsok/processed-order?s=https%3A%2F%2Fdsok-tickets.myshopify.com%2F99449143622%2Forders%2F46eb52b9276956a31d0f482265f3be31%2Fauthenticate%3Fkey%3Dshcct_aGU0cStNMU81S3M1Q3R6eG1KUXJ1dlM0RWYwS3dtZ1hGR3Q5UWo3Q1JCU01JeURKQUphY1ZFWmJZT0Y4ZEVqc01pMDBpVllaWVlUaUQxWFQwWDNTTXlEUS9sOU40UGcySFJ3UUdqRFVjRkdIRU8wN1pMN0FydHJiNGI3YWlSeWhWdG9WNnZLNGhFVDZISk5PblBBamRWR3FuRVRDTzRuVE5YWEFZekpxNnh5TWFTdHdOZGlTZ1R4R1ZNUGtlZEt3UTl4ZklXY09tZ1ZzQno2ZDZBWVRlcnpiY3NBU1AxNHZER0doL3FVT3VTQ2xzYlRSbWJrWXU5OEJXSjA4SjZBM3dIZHFIMTg4K2V0SUpaM1c4UXl0SStiNlZzS3VzMXZXTnJTMTBOZThHOU1MS2J3cHQrRmpxMFFKK01PM2NmY0w3SXJkejM4OEdtTVlsMjhhNytnVTN2V2l2eUNxQXFHMkdWNml0dU0yMy95K2N4YUN0SkJMdEZCdVN0T21sNklkY1NpaStDR1Rib0NOdnBzOGdOZkZVZkZMeTEreDhnT3B1K2g1YVdTY1JHK0RVS3B2MW1BcXp6NFBTR2w1Q0wyZGR6bkZUM09uOE5mTE9YUGFiNjgvVWI2dDdhZnZCS29EbEFEZEQzTUhvTmFKVG1oMmlMY2xVUmNiTElSWmFrWmx6U05DUWlVdmtuRm50MjEyOFNkektnMjJlWnR0alE1aEwwTFhoRCtCNGFTa1BTaVhwTmNWQmw2eHoyUE14bmtQZzZ2UHJLWT0tLWlpMzE5bCtrcTIyNE83MTAtLVd3Z0l0bVl2YVBYMFlYM0Q4OGcvU3c9PQ&syclid=ce776368-3d66-4ff2-81c0-a11a781ecda7";

export const ORDER_MANAGEMENT_FIELD_SCHEMA = {
  status: "",
  type: "none",
  integrator: "none",
  martekUnregisteredIco: "",
  api: {
    createOrder: "",
  },
  // Optional Dotykačka import language mapping (off by default): { "<dotykackaLang>": "<ourLang>" }.
  languageMapping: {
    enabled: false,
    map: {},
  },
};

export const PRODUCTS_MANAGEMENT_FIELD_SCHEMA = {
  status: "",
  type: "none",
  // DSOK-456: how the backend reaches OBERON — "direct" (backend -> OBERON via
  // fetchProductsUrl, needs a public fixed IP) or "dla" (on-premise DLA fetches locally
  // and pushes to the backend, identified by connectionCode).
  connectionMode: "direct",
  connectionCode: "",
  credentials: {},
  additionalVariables: {},
  api: {
    fetchProductsUrl: "",
  },
};

export const EXTERNAL_ORDERS_FIELD_SCHEMA = {
  isActive: false,
  source: "none",
  signature: "",
};

export const INTEGRATIONS_FIELD_SCHEMA = {
  orderManagement: ORDER_MANAGEMENT_FIELD_SCHEMA,
  productsManagement: PRODUCTS_MANAGEMENT_FIELD_SCHEMA,
  externalOrders: EXTERNAL_ORDERS_FIELD_SCHEMA,
};

const SmartkioskIntegrationFields = ({
  handleChange,
  expanded,
  hideSectionTitle = false,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext();
  const [fetching, setFetching] = useState();
  const [dotykackaBranches, setDotykackaBranches] = useState();
  const [dotykackaGenerateTokenUrl, setDotykackaGenerateTokenUrl] =
    useState("");
  const { showDialog, hideDialog } = useJumboDialog();
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState("orders");
  const customerId = values?.customer?.customerId;

  // WooCommerce dedicated block state
  const [wooSecretInput, setWooSecretInput] = useState("");
  const [wooBusy, setWooBusy] = useState(false);
  const [wooStatus, setWooStatus] = useState(null);

  // Dotykačka optional language mapping (integrations.orderManagement.languageMapping). The textarea
  // edits a JSON object { "<dotykackaLang>": "<ourLang>" }; kept in local state so invalid JSON never
  // reaches Formik — only a valid parsed object is written to languageMapping.map.
  const [langMapText, setLangMapText] = useState(null);
  const [langMapError, setLangMapError] = useState("");

  // DSOK-456: OBERON product sync via DLA
  const [dlaImportBusy, setDlaImportBusy] = useState(false);

  const shopifyApiUrl = useMemo(() => {
    const beDomain =
      values?.customer?.beDomain ??
      values?.customer?.dsokSettings?.customer?.beDomain ??
      "";
    const customerCode =
      values?.customer?.code ??
      values?.customer?.dsokSettings?.customer?.code ??
      "";
    const safeBeDomain = String(beDomain).replace(/\/+$/, "");
    if (!safeBeDomain || !customerCode) return "";
    return `${safeBeDomain}/api/v1/external/shopify/${customerCode}`;
  }, [
    values?.customer?.beDomain,
    values?.customer?.dsokSettings?.customer?.beDomain,
    values?.customer?.code,
    values?.customer?.dsokSettings?.customer?.code,
  ]);

  const shopifyTicketUrlTemplate = useMemo(() => {
    const beDomain =
      values?.customer?.beDomain ??
      values?.customer?.dsokSettings?.customer?.beDomain ??
      "";
    const safeBeDomain = String(beDomain).replace(/\/+$/, "");
    if (!safeBeDomain) return SHOPIFY_TICKET_URL_TEMPLATE_PATH;
    return `${safeBeDomain}${SHOPIFY_TICKET_URL_TEMPLATE_PATH}`;
  }, [
    values?.customer?.beDomain,
    values?.customer?.dsokSettings?.customer?.beDomain,
  ]);

  const handleCopyShopifyApiUrl = async () => {
    if (!shopifyApiUrl) return;
    try {
      await navigator.clipboard.writeText(shopifyApiUrl);
      enqueueSnackbar(
        t("pages.dsokDetails.integrations.externalOrders.shopifyApiUrlCopied", {
          defaultValue: "Shopify API URL was copied to clipboard",
        }),
        { variant: "success" },
      );
    } catch (_e) {
      enqueueSnackbar(
        t(
          "pages.dsokDetails.integrations.externalOrders.shopifyApiUrlCopyError",
          {
            defaultValue: "Could not copy Shopify API URL",
          },
        ),
        { variant: "error" },
      );
    }
  };

  const handleCopyShopifyTicketUrl = async () => {
    try {
      await navigator.clipboard.writeText(shopifyTicketUrlTemplate);
      enqueueSnackbar(
        t("pages.dsokDetails.integrations.externalOrders.ticketUrlCopied", {
          defaultValue: "Ticket URL was copied to clipboard",
        }),
        { variant: "success" },
      );
    } catch (_e) {
      enqueueSnackbar(
        t("pages.dsokDetails.integrations.externalOrders.ticketUrlCopyError", {
          defaultValue: "Could not copy Ticket URL",
        }),
        { variant: "error" },
      );
    }
  };

  const handleCopyDotykackaRedirectUrl = async () => {
    if (!dotykackaGenerateTokenUrl) return;
    try {
      await navigator.clipboard.writeText(dotykackaGenerateTokenUrl);
      enqueueSnackbar(
        t("pages.dsokDetails.integrations.dotykacka.redirectUrlCopied", {
          defaultValue: "Redirect URL was copied to clipboard",
        }),
        { variant: "success" },
      );
    } catch (_e) {
      enqueueSnackbar(
        t("pages.dsokDetails.integrations.dotykacka.redirectUrlCopyError", {
          defaultValue: "Could not copy Redirect URL",
        }),
        { variant: "error" },
      );
    }
  };

  const handleOpenProcessedOrderPreview = () => {
    window.open(PROCESSED_ORDER_PREVIEW_URL, "_blank", "noopener,noreferrer");
  };

  const selectedProductsTypeConfig = useMemo(() => {
    const typeCode = values?.integrations?.productsManagement?.type;
    return DSOK_PRODUCTS_MANAGEMENT_TYPES.find((t) => t.code === typeCode);
  }, [values?.integrations?.productsManagement?.type]);

  const isWooCommerce = selectedProductsTypeConfig?.code === "woocommerce";
  const wooHasStoredSecret = Boolean(
    values?.integrations?.productsManagement?.credentials?.password?.value,
  );

  // OBERON always communicates through the on-premise DLA (never a public fixed IP), so the
  // connection mode is not user-selectable — it is forced to "dla" (see the effect below).
  const isOberon = selectedProductsTypeConfig?.code === "oberon";
  const oberonIsDla = isOberon;

  // DLA options for the connection-code select: the customer's main DLA plus every kiosk
  // that has its own connectionCode. Labels are names ("DLA zákazníka", "DLA - Kiosk 01"),
  // never the raw code.
  const dlaConnectionOptions = useMemo(() => {
    const options = [];
    const seen = new Set();
    const push = (value, label) => {
      const v = value != null ? String(value).trim() : "";
      if (!v || seen.has(v)) return;
      seen.add(v);
      options.push({ value: v, label });
    };
    push(
      values?.customer?.dlaConnectionCode,
      t("pages.dsokDetails.integrations.oberon.dlaCustomer", {
        defaultValue: "DLA zákazníka",
      }),
    );
    const devices = values?.customer?.devices || {};
    Object.keys(devices).forEach((key) => {
      const d = devices[key] || {};
      const kioskName =
        d.name && String(d.name).trim()
          ? String(d.name).trim()
          : `Kiosk ${String(key).padStart(2, "0")}`;
      push(d.connectionCode, `DLA - ${kioskName}`);
    });
    return options;
  }, [
    values?.customer?.dlaConnectionCode,
    values?.customer?.devices,
    t,
  ]);

  // OBERON is always routed through the DLA; keep the stored connection mode consistent
  // (covers configs saved before the mode selector was removed, and new OBERON configs).
  useEffect(() => {
    if (
      isOberon &&
      values?.integrations?.productsManagement?.connectionMode !== "dla"
    ) {
      setFieldValue("integrations.productsManagement.connectionMode", "dla");
    }
  }, [
    isOberon,
    values?.integrations?.productsManagement?.connectionMode,
    setFieldValue,
  ]);

  // Trigger an OBERON product sync (import/delta) on the on-premise DLA.
  const runDlaImport = async (mode) => {
    const connectionCode =
      values?.integrations?.productsManagement?.connectionCode;
    if (!connectionCode || !String(connectionCode).trim()) {
      enqueueSnackbar(
        t("pages.dsokDetails.integrations.oberon.connectionCodeRequired", {
          defaultValue: "Zadajte DLA connection code.",
        }),
        { variant: "warning" },
      );
      return;
    }
    setDlaImportBusy(true);
    try {
      const res = await syncOberonViaDla({
        customerId,
        connectionCode: String(connectionCode).trim(),
        mode,
      });
      if (res?.success) {
        enqueueSnackbar(
          t("pages.dsokDetails.integrations.oberon.dlaTriggered", {
            defaultValue: "Sync cez DLA spustený.",
          }),
          { variant: "success" },
        );
      } else {
        enqueueSnackbar(
          `${t("pages.dsokDetails.integrations.oberon.dlaFailed", {
            defaultValue: "Spustenie cez DLA zlyhalo",
          })}: ${res?.reason || ""}`,
          { variant: "error" },
        );
      }
    } catch (e) {
      const reason = e?.response?.data?.reason || e?.message || "";
      const msg =
        reason === "DLA_NOT_CONNECTED"
          ? t("pages.dsokDetails.integrations.oberon.dlaNotConnected", {
              defaultValue: "DLA nie je pripojená.",
            })
          : `${t("pages.dsokDetails.integrations.oberon.dlaFailed", {
              defaultValue: "Spustenie cez DLA zlyhalo",
            })}: ${reason}`;
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setDlaImportBusy(false);
    }
  };

  const showProductsFetchUrl = Boolean(selectedProductsTypeConfig?.showUrl);
  const showImportProductsButton = Boolean(
    selectedProductsTypeConfig?.showImportProductsButton,
  );
  const showProductsHelpText = Boolean(
    selectedProductsTypeConfig?.showHelpText &&
    selectedProductsTypeConfig?.helpText,
  );
  const showProductsCredentials = Boolean(
    selectedProductsTypeConfig?.showCredentials &&
    selectedProductsTypeConfig?.credentials &&
    typeof selectedProductsTypeConfig.credentials === "object",
  );
  const showProductsAuthEnabledSwitch = Boolean(
    showProductsCredentials &&
    selectedProductsTypeConfig?.authEnabled?.type === "switch",
  );
  const productsAuthEnabled = Boolean(
    values?.integrations?.productsManagement?.authEnabled ?? true,
  );
  const productsCredentialsConfig = useMemo(
    () => selectedProductsTypeConfig?.credentials || null,
    [selectedProductsTypeConfig],
  );

  const showAdditionalVariables = Boolean(
    selectedProductsTypeConfig?.showAdditionalVariables &&
    selectedProductsTypeConfig?.additionalVariables &&
    typeof selectedProductsTypeConfig.additionalVariables === "object",
  );
  const additionalVariablesConfig = useMemo(
    () => selectedProductsTypeConfig?.additionalVariables || null,
    [selectedProductsTypeConfig],
  );

  const getProductsCredentialValue = (key) => {
    const creds = values?.integrations?.productsManagement?.credentials;
    const entry = creds?.[key];
    if (entry && typeof entry === "object" && !Array.isArray(entry)) {
      return entry?.value ?? "";
    }
    if (typeof entry === "string") return entry;
    return "";
  };

  // Ensure stored credential objects contain metadata (type/label) without touching values.
  // This helps keep the persisted shape stable across saves while respecting "never clear/reset".
  useEffect(() => {
    if (!showProductsCredentials) return;
    const credsRaw = values?.integrations?.productsManagement?.credentials;
    if (!credsRaw || typeof credsRaw !== "object" || Array.isArray(credsRaw))
      return;

    const toCheck = ["username", "password"];
    let changed = false;
    const nextCreds = { ...credsRaw };

    for (const key of toCheck) {
      const cfg = productsCredentialsConfig?.[key];
      if (!cfg) continue;

      const entry = nextCreds[key];
      if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
      if (entry.value === undefined) continue; // don't create/modify empty values

      const patched = { ...entry };
      if (patched.type === undefined && cfg?.type) {
        patched.type = cfg.type;
        changed = true;
      }
      if (patched.label === undefined && cfg?.label) {
        patched.label = cfg.label;
        changed = true;
      }
      if (changed) nextCreds[key] = patched;
    }

    if (changed) {
      setFieldValue("integrations.productsManagement.credentials", nextCreds);
    }
  }, [
    productsCredentialsConfig,
    setFieldValue,
    showProductsCredentials,
    values?.integrations?.productsManagement?.credentials,
  ]);

  const setProductsCredentialValue = (key, value) => {
    const credsRaw = values?.integrations?.productsManagement?.credentials;
    const creds =
      credsRaw && typeof credsRaw === "object" && !Array.isArray(credsRaw)
        ? credsRaw
        : {};

    const existing = creds?.[key];
    const existingObj =
      existing && typeof existing === "object" && !Array.isArray(existing)
        ? existing
        : {};

    const cfg = productsCredentialsConfig?.[key];

    setFieldValue("integrations.productsManagement.credentials", {
      ...creds,
      [key]: {
        ...existingObj,
        ...(cfg?.type !== undefined && existingObj.type === undefined
          ? { type: cfg.type }
          : {}),
        ...(cfg?.label !== undefined && existingObj.label === undefined
          ? { label: cfg.label }
          : {}),
        value,
      },
    });
  };

  const getAdditionalVariableValue = (key) => {
    const vars = values?.integrations?.productsManagement?.additionalVariables;
    const entry = vars?.[key];
    if (entry && typeof entry === "object" && !Array.isArray(entry)) {
      const value = entry?.value;
      // Handle boolean values for switch type
      if (typeof value === "boolean") return value;
      if (value === "true" || value === true) return true;
      if (value === "false" || value === false) return false;
      return value ?? "";
    }
    if (typeof entry === "string") return entry;
    if (typeof entry === "boolean") return entry;
    return "";
  };

  const setAdditionalVariableValue = (key, value) => {
    const varsRaw =
      values?.integrations?.productsManagement?.additionalVariables;
    const vars =
      varsRaw && typeof varsRaw === "object" && !Array.isArray(varsRaw)
        ? varsRaw
        : {};

    const existing = vars?.[key];
    const existingObj =
      existing && typeof existing === "object" && !Array.isArray(existing)
        ? existing
        : {};

    const cfg = additionalVariablesConfig?.[key];

    setFieldValue("integrations.productsManagement.additionalVariables", {
      ...vars,
      [key]: {
        ...existingObj,
        ...(cfg?.type !== undefined && existingObj.type === undefined
          ? { type: cfg.type }
          : {}),
        ...(cfg?.label !== undefined && existingObj.label === undefined
          ? { label: cfg.label }
          : {}),
        ...(cfg?.description !== undefined &&
        existingObj.description === undefined
          ? { description: cfg.description }
          : {}),
        value,
      },
    });
  };

  const fetchProducts = async (url) => {
    hideDialog();
    setFetching(true);
    const fetchUrl = `${url}${url?.endsWith("/") ? "" : "/"}${
      values.customer.code
    }`;
    try {
      await fetchDSOKProducts(fetchUrl);
      const message = `${t("pages.dsokDetails.fetchProducts")} ${t(
        "notifications.wasSuccessful",
      )}`;
      enqueueSnackbar(message, {
        variant: "success",
      });
    } catch (error) {
      const message = `${t("pages.dsokDetails.fetchProducts")} ${t(
        "notifications.wasFailed",
      )}`;
      enqueueSnackbar(message, {
        variant: "error",
      });
    }
    setFetching(false);
  };

  // WooCommerce: dry-run connection test (validates credentials + returns counts/sample).
  const handleWooTest = async () => {
    if (!customerId) return;
    setWooBusy(true);
    setWooStatus(null);
    try {
      const res = await testWooConnection(
        customerId,
        values?.integrations?.productsManagement,
      );
      const d = res?.data || {};
      setWooStatus({
        kind: "test",
        ok: true,
        summary: `${d.productsMapped ?? 0} produktov, ${
          d.categoriesFetched ?? 0
        } kategórií`,
        data: d,
      });
      enqueueSnackbar(
        `WooCommerce test OK — ${d.productsMapped ?? 0} produktov, ${
          d.categoriesFetched ?? 0
        } kategórií`,
        { variant: "success" },
      );
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "WooCommerce test zlyhal";
      setWooStatus({ kind: "test", ok: false, summary: msg });
      enqueueSnackbar(`WooCommerce test zlyhal: ${msg}`, { variant: "error" });
    }
    setWooBusy(false);
  };

  // WooCommerce: start async import and poll the job until it finishes.
  const handleWooImport = async () => {
    if (!customerId) return;
    hideDialog();
    setWooBusy(true);
    setWooStatus({ kind: "import", running: true, summary: "Import spustený…" });
    try {
      const job = await importWooProducts(
        customerId,
        values?.integrations?.productsManagement,
      );
      const jobId = job?.jobId;
      if (!jobId) {
        throw new Error("Chýba jobId");
      }

      // Poll status (max ~5 min).
      const started = Date.now();
      const poll = async () => {
        const status = await getWooSyncStatus(jobId);
        const st = status?.status;
        if (st === "done") {
          const r = status?.progress?.result || {};
          setWooStatus({
            kind: "import",
            running: false,
            ok: true,
            summary: `Hotovo — ${r.productsActive ?? 0} aktívnych produktov, ${
              r.categoriesUpserted ?? 0
            } kategórií, ${r.imagesUploaded ?? 0} obrázkov (${
              r.deactivated ?? 0
            } deaktivovaných)`,
            data: r,
          });
          enqueueSnackbar("WooCommerce import dokončený", {
            variant: "success",
          });
          setWooBusy(false);
          return;
        }
        if (st === "failed" || st === "canceled") {
          const msg = status?.error?.message || status?.progress?.message || st;
          setWooStatus({
            kind: "import",
            running: false,
            ok: false,
            summary: `Import zlyhal: ${msg}`,
          });
          enqueueSnackbar(`WooCommerce import zlyhal: ${msg}`, {
            variant: "error",
          });
          setWooBusy(false);
          return;
        }
        // still pending/running
        setWooStatus({
          kind: "import",
          running: true,
          summary: status?.progress?.message || "Prebieha import…",
        });
        if (Date.now() - started > 5 * 60 * 1000) {
          setWooStatus({
            kind: "import",
            running: false,
            ok: false,
            summary:
              "Import beží dlhšie ako 5 minút — pokračuje na pozadí, skontrolujte neskôr.",
          });
          setWooBusy(false);
          return;
        }
        setTimeout(poll, 2500);
      };
      setTimeout(poll, 2000);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "WooCommerce import zlyhal";
      setWooStatus({ kind: "import", running: false, ok: false, summary: msg });
      enqueueSnackbar(`WooCommerce import zlyhal: ${msg}`, { variant: "error" });
      setWooBusy(false);
    }
  };

  const handleMakeRefreshToken = async (currentCustomerId, beDomain) => {
    const result = await makeDotykackaRefreshToken(currentCustomerId, beDomain);
    if (result?.redirectUrl) {
      setDotykackaGenerateTokenUrl(result.redirectUrl);
    }
    return result;
  };

  useEffect(() => {
    const isDotykacka =
      values?.integrations?.orderManagement?.type === "dotykacka";
    if (!isDotykacka || !customerId) return;

    let isMounted = true;
    (async () => {
      try {
        const result = await handleMakeRefreshToken(
          customerId,
          values?.customer?.beDomain,
        );
        if (isMounted && !result?.redirectUrl) {
          setDotykackaGenerateTokenUrl("");
        }
      } catch (error) {
        if (isMounted) {
          setDotykackaGenerateTokenUrl("");
        }
        console.log("Error creating Dotykacka refresh token URL:", error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [
    customerId,
    values?.customer?.beDomain,
    values?.integrations?.orderManagement?.type,
  ]);

  useEffect(() => {
    const shouldFetchBranches =
      values?.integrations?.orderManagement?.type === "dotykacka" &&
      !!values?.integrations?.orderManagement?.api?.branchId &&
      !!customerId;

    if (!shouldFetchBranches) return;

    let isMounted = true;
    (async () => {
      try {
        const result = await getDotykackaBranch(
          customerId,
          values?.customer?.beDomain,
        );
        if (isMounted) {
          setDotykackaBranches(result?.branches);
        }
      } catch (error) {
        console.log("Error fetching Dotykacka branches:", error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [
    values?.integrations?.orderManagement?.type,
    values?.integrations?.orderManagement?.api?.branchId,
    customerId,
    values?.customer?.beDomain,
  ]);

  const content = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={12}>
        <TabContext value={activeTab}>
          <Div sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList onChange={(_, newTab) => setActiveTab(newTab)}>
              <Tab
                label={t("pages.dsokDetails.integrations.orders.title")}
                value={"orders"}
              />
              <Tab
                label={t("pages.dsokDetails.integrations.products.title")}
                value={"products"}
              />
              <Tab
                label={t("pages.dsokDetails.integrations.externalOrders.title")}
                value={"externalOrders"}
              />
            </TabList>
          </Div>

          <TabPanel value={"orders"} sx={{ p: 0 }}>
            <Grid item xs={12} md={12} lg={12} container spacing={2}>
              <Grid item xs={12} md={12} lg={12}>
                <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Field
                        name="integrations.orderManagement.status"
                        component={Switch}
                        onChange={(e) =>
                          setFieldValue(
                            "integrations.orderManagement.status",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label={t("pages.screens.status")}
                    checked={values?.integrations?.orderManagement?.status}
                  />
                </Div>
              </Grid>
              {["dsokIntegrationStaging", "dsokIntegration"].includes(
                values?.integrations?.orderManagement?.type,
              ) && (
                <Grid item xs={12} md={12} lg={12}>
                  <Alert variant="outlined" severity="warning">
                    {t("pages.dsokDetails.integrations.noUrlNeeded")}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} md={2} lg={4}>
                <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                  <JumboTextField
                    select
                    required
                    InputLabelProps={{ required: false }}
                    name="integrations.orderManagement.type"
                    label={
                      <Label text={t("pages.dsokDetails.type")} required />
                    }
                  >
                    {DSOK_ORDER_MANAGEMENT_TYPES.map((type, index) => {
                      return (
                        <MenuItem key={index} value={type.code}>
                          {type.company}
                        </MenuItem>
                      );
                    })}
                  </JumboTextField>
                </FormControl>
              </Grid>
              {["dsokIntegrationStaging", "dsokIntegration"].includes(
                values?.integrations?.orderManagement?.type,
              ) && (
                <Grid item xs={12} md={2} lg={4}>
                  <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                    <JumboTextField
                      select
                      name="integrations.orderManagement.integrator"
                      label={t(
                        "pages.dsokDetails.integrations.integrator",
                        { defaultValue: "Integrator" },
                      )}
                    >
                      <MenuItem value="none">none</MenuItem>
                      <MenuItem value="Martek">Martek</MenuItem>
                      <MenuItem value="BodyColor">BodyColor</MenuItem>
                    </JumboTextField>
                  </FormControl>
                </Grid>
              )}
              {values?.integrations?.orderManagement?.integrator ===
                "Martek" && (
                <Grid item xs={12} md={2} lg={4}>
                  <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                    <JumboTextField
                      fullWidth
                      type="number"
                      name="integrations.orderManagement.martekUnregisteredIco"
                      label={
                        <Label
                          text={t(
                            "pages.dsokDetails.integrations.martekUnregisteredIco",
                            { defaultValue: "IČO pre neregistrovaných" },
                          )}
                          helpText={t(
                            "pages.dsokDetails.integrations.martekUnregisteredIcoHelp",
                            {
                              defaultValue:
                                "Toto IČO sa používa pre neregistrovaných zákazníkov a slúži na spárovanie objednávky v systéme Martek na neregistrovaných zákazníkov.",
                            },
                          )}
                          required={false}
                        />
                      }
                    />
                  </FormControl>
                </Grid>
              )}
              {values?.integrations?.orderManagement?.type !== "dotykacka" &&
                values?.integrations?.orderManagement?.type !== "oberon" && (
                  <Grid item xs={12} md={6}>
                    <Div sx={{ mt: 1, mb: 1 }}>
                      <JumboTextField
                        fullWidth
                        name="integrations.orderManagement.api.createOrder"
                        label={t("pages.dsokDetails.createOrder")}
                      />
                    </Div>
                  </Grid>
                )}
              {/* OBERON orders reuse the OBERON connection configured on the Products tab
                  (connection mode / connection code), so no order-specific URL is needed. */}
              {values?.integrations?.orderManagement?.type === "oberon" && (
                <Grid item xs={12} md={12} lg={12}>
                  <Alert variant="outlined" severity="info" sx={{ mt: 1, mb: 1 }}>
                    {t("pages.dsokDetails.integrations.oberon.orderConnectionInfo", {
                      defaultValue:
                        "Spojenie na OBERON (vrátane odosielania cez DLA) sa nastavuje na karte Produkty. Objednávky používajú to isté spojenie.",
                    })}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12} md={12} lg={12}></Grid>
              {values?.integrations?.orderManagement?.type === "storyous" && (
                <>
                  <Grid item xs={12} md={4} lg={2}>
                    <JumboTextField
                      fullWidth
                      name="integrations.orderManagement.api.merchantID"
                      label={t("pages.dsokDetails.merchantID")}
                    />
                  </Grid>
                  <Grid item xs={12} md={4} lg={2}>
                    <JumboTextField
                      fullWidth
                      name="integrations.orderManagement.api.placeID"
                      label={t("pages.dsokDetails.placeID")}
                    />
                  </Grid>
                </>
              )}
              {values?.integrations?.orderManagement?.type === "dotykacka" && (
                <>
                  <Grid item xs={12} md={12} lg={12}>
                    <Alert variant="outlined" severity="info" sx={{ mt: 1, mb: 1 }}>
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          pl: 2.5,
                          "& li": { typography: "body2" },
                        }}
                      >
                        <Box component="li">
                          {t(
                            "pages.dsokDetails.integrations.dotykacka.redirectUrlHelp",
                          )}
                        </Box>
                        <Box component="li" sx={{ mt: 1 }}>
                          {t(
                            "pages.dsokDetails.integrations.dotykacka.autoGeneratedFieldsHelp",
                          )}
                        </Box>
                      </Box>
                    </Alert>
                  </Grid>
                  {dotykackaGenerateTokenUrl ? (
                    <Grid item xs={12} md={12} lg={12}>
                      <Div
                        sx={{
                          mt: 0,
                          mb: 1,
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                        }}
                      >
                        <TextField
                          fullWidth
                          multiline
                          minRows={2}
                          label={t(
                            "pages.dsokDetails.integrations.dotykacka.redirectUrlLabel",
                            { defaultValue: "Redirect URL" },
                          )}
                          value={dotykackaGenerateTokenUrl}
                          InputProps={{ readOnly: true }}
                        />
                        <Tooltip
                          title={t(
                            "pages.dsokDetails.integrations.dotykacka.copyRedirectUrl",
                            { defaultValue: "Copy" },
                          )}
                        >
                          <IconButton
                            aria-label={t(
                              "pages.dsokDetails.integrations.dotykacka.copyRedirectUrl",
                              { defaultValue: "Copy" },
                            )}
                            size="small"
                            onClick={() => void handleCopyDotykackaRedirectUrl()}
                            sx={{ mt: 1 }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Div>
                    </Grid>
                  ) : null}
                  <Grid item xs={12} md={12} lg={2}>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ boxShadow: "none" }}
                      disabled={fetching}
                      onClick={async () => {
                        try {
                          setFetching(true);
                          const branchResult = await getDotykackaBranch(
                            customerId,
                            values?.customer?.beDomain,
                          );
                          setDotykackaBranches(branchResult?.branches);
                        } catch (error) {
                          console.log("Error:", error);
                        } finally {
                          setFetching(false);
                        }
                      }}
                    >
                      {t("pages.dsokDetails.getBranch")}
                    </Button>
                  </Grid>
                  {Array.isArray(dotykackaBranches) &&
                    dotykackaBranches.length > 0 && (
                      <Grid item xs={12} md={12} lg={2}>
                        <Div sx={{ mt: 1, mb: 1 }}>
                          <JumboTextField
                            select
                            fullWidth
                            name="integrations.orderManagement.api.branchId"
                            label={t("pages.dsokDetails.branch")}
                          >
                            {dotykackaBranches.map((branch, index) => {
                              const isObject =
                                typeof branch === "object" && branch !== null;
                              const value = isObject
                                ? branch.id ||
                                  branch.branchId ||
                                  branch.code ||
                                  branch.name ||
                                  index
                                : branch;
                              const label = isObject
                                ? branch.name ||
                                  branch.label ||
                                  branch.title ||
                                  branch.id ||
                                  String(value)
                                : String(branch);
                              return (
                                <MenuItem key={value} value={value}>
                                  {label}
                                </MenuItem>
                              );
                            })}
                          </JumboTextField>
                        </Div>
                      </Grid>
                    )}
                  <Grid item xs={12} md={12} lg={12}></Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <Div sx={{ mt: 1, mb: 1 }}>
                      <JumboTextField
                        fullWidth
                        name="integrations.orderManagement.api.cloudId"
                        label={t("pages.dsokDetails.cloudId")}
                        InputProps={{ readOnly: true }}
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <Div sx={{ mt: 1, mb: 1 }}>
                      <JumboTextField
                        fullWidth
                        name="integrations.orderManagement.api.refreshToken"
                        label={t("pages.dsokDetails.refreshToken")}
                        InputProps={{ readOnly: true }}
                      />
                    </Div>
                  </Grid>

                  {/* Optional language mapping: Dotykačka lang key → our lang key (off by default). */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1 }}>
                      {t("pages.dsokDetails.integrations.dotykacka.languageMappingTitle", {
                        defaultValue: "Mapovanie jazykov (voliteľné)",
                      })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Tooltip
                      arrow
                      title={t("pages.dsokDetails.integrations.dotykacka.languageMappingEnabledHelp", {
                        defaultValue:
                          "Voliteľné. Štandardne vypnuté — preklady z Dotykačky sa importujú 1:1. Zapnite len ak zákazník potrebuje jazyk, ktorý Dotykačka nemá (napr. maďarčinu urobenú v Dotykačke pod iným jazykom).",
                      })}
                    >
                      <Box component="span">
                        <FormControlLabel
                          control={
                            <Field
                              name="integrations.orderManagement.languageMapping.enabled"
                              component={Switch}
                              onChange={(e) =>
                                setFieldValue(
                                  "integrations.orderManagement.languageMapping.enabled",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={t("pages.dsokDetails.integrations.dotykacka.languageMappingEnabled", {
                            defaultValue: "Povoliť mapovanie jazykov",
                          })}
                          checked={
                            values?.integrations?.orderManagement?.languageMapping?.enabled === true
                          }
                        />
                      </Box>
                    </Tooltip>
                  </Grid>
                  {values?.integrations?.orderManagement?.languageMapping?.enabled === true && (
                    <Grid item xs={12} md={8} lg={6}>
                      <Tooltip
                        arrow
                        title={t("pages.dsokDetails.integrations.dotykacka.languageMapHelp", {
                          defaultValue:
                            'JSON objekt: kľúč = jazyk v Dotykačke, hodnota = náš jazyk. Napr. { "cs": "hu" } znamená, že to, čo má Dotykačka pod „cs", sa u nás uloží pod „hu" (pôvodný jazyk sa neuloží — je to náhrada). Uvádzajte len jazyky, ktoré treba premapovať; jazyky, ktoré sa nemenia, netreba uvádzať — importujú sa 1:1.',
                        })}
                      >
                        <Box component="span" sx={{ display: "block" }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label={t("pages.dsokDetails.integrations.dotykacka.languageMap", {
                              defaultValue: "Mapovanie jazykov (JSON)",
                            })}
                            placeholder={'{ "cs": "hu" }'}
                            value={
                              langMapText == null
                                ? JSON.stringify(
                                    values?.integrations?.orderManagement?.languageMapping?.map || {},
                                    null,
                                    2,
                                  )
                                : langMapText
                            }
                            error={!!langMapError}
                            helperText={langMapError || " "}
                            onChange={(e) => {
                              const txt = e.target.value;
                              setLangMapText(txt);
                              if (!txt.trim()) {
                                setLangMapError("");
                                setFieldValue("integrations.orderManagement.languageMapping.map", {});
                                return;
                              }
                              try {
                                const obj = JSON.parse(txt);
                                const ok =
                                  obj &&
                                  typeof obj === "object" &&
                                  !Array.isArray(obj) &&
                                  Object.values(obj).every((v) => typeof v === "string");
                                if (ok) {
                                  setLangMapError("");
                                  setFieldValue(
                                    "integrations.orderManagement.languageMapping.map",
                                    obj,
                                  );
                                } else {
                                  setLangMapError(
                                    t("pages.dsokDetails.integrations.dotykacka.languageMapInvalid", {
                                      defaultValue:
                                        'Očakáva sa JSON objekt reťazec→reťazec, napr. { "cs": "hu" }.',
                                    }),
                                  );
                                }
                              } catch (err) {
                                setLangMapError(
                                  t("pages.dsokDetails.integrations.dotykacka.languageMapBadJson", {
                                    defaultValue: "Neplatný JSON.",
                                  }),
                                );
                              }
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </TabPanel>
          <TabPanel value={"products"} sx={{ p: 0 }}>
            <Grid item xs={12} md={12} lg={12} container spacing={2}>
              <Grid item xs={12} md={12} lg={12}>
                <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Field
                        name="integrations.productsManagement.status"
                        component={Switch}
                        onChange={(e) =>
                          setFieldValue(
                            "integrations.productsManagement.status",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label={t("pages.screens.status")}
                    checked={
                      values?.integrations?.productsManagement?.status === true
                    }
                  />
                </Div>
              </Grid>
              <Grid item xs={12} md={4} lg={4}>
                <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                  <JumboTextField
                    select
                    required
                    InputLabelProps={{ required: false }}
                    name="integrations.productsManagement.type"
                    label={
                      <Label text={t("pages.dsokDetails.type")} required />
                    }
                  >
                    {DSOK_PRODUCTS_MANAGEMENT_TYPES.map((type, index) => {
                      return (
                        <MenuItem key={index} value={type.code}>
                          {type.company}
                        </MenuItem>
                      );
                    })}
                  </JumboTextField>
                </FormControl>
              </Grid>
              {showProductsHelpText && (
                <Grid item xs={12} md={12} lg={12}>
                  <Alert variant="outlined" severity="info">
                    {String(selectedProductsTypeConfig.helpText)}
                  </Alert>
                </Grid>
              )}
              {showProductsCredentials && (
                <>
                  {showProductsAuthEnabledSwitch && (
                    <Grid item xs={12} md={12} lg={12}>
                      <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={productsAuthEnabled}
                              onChange={(e) =>
                                setFieldValue(
                                  "integrations.productsManagement.authEnabled",
                                  e.target.checked,
                                )
                              }
                            />
                          }
                          label={
                            selectedProductsTypeConfig?.authEnabled?.label ||
                            "Auth enabled"
                          }
                        />
                      </Div>
                    </Grid>
                  )}

                  {productsAuthEnabled && (
                    <>
                      {Object.entries(
                        selectedProductsTypeConfig.credentials,
                      ).map(([key, cfg]) => {
                        if (key !== "username" && key !== "password")
                          return null;

                        return (
                          <Grid
                            key={key}
                            item
                            xs={12}
                            md={isOberon ? 4 : 6}
                            lg={isOberon ? 4 : 6}
                          >
                            <Div sx={{ mt: 1, mb: 1 }}>
                              <TextField
                                fullWidth
                                size="medium"
                                type={cfg?.type || "text"}
                                label={cfg?.label || key}
                                value={getProductsCredentialValue(key)}
                                onChange={(e) =>
                                  setProductsCredentialValue(
                                    key,
                                    e.target.value,
                                  )
                                }
                              />
                            </Div>
                          </Grid>
                        );
                      })}
                    </>
                  )}
                </>
              )}
              {showAdditionalVariables && (
                <>
                  {Object.entries(
                    selectedProductsTypeConfig.additionalVariables,
                  ).map(([key, cfg]) => {
                    if (cfg?.type === "switch") {
                      // A switch with defaultOn:true renders ON when the customer has no
                      // stored value yet (unset === "") — matching the backend default.
                      const rawSwitchValue = getAdditionalVariableValue(key);
                      const isUnset =
                        rawSwitchValue === "" || rawSwitchValue == null;
                      const switchChecked = isUnset
                        ? cfg?.defaultOn === true
                        : rawSwitchValue === true || rawSwitchValue === "true";
                      return (
                        <Grid
                          key={key}
                          item
                          xs={12}
                          md={isOberon ? 4 : 6}
                          lg={isOberon ? 4 : 6}
                        >
                          <Div sx={{ mt: 1, mb: 1, mx: 0.5 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={switchChecked}
                                  onChange={(e) =>
                                    setAdditionalVariableValue(
                                      key,
                                      e.target.checked,
                                    )
                                  }
                                />
                              }
                              label={
                                <Label
                                  text={cfg?.label || key}
                                  helpText={cfg?.description || ""}
                                  sx={{ color: "text.primary" }}
                                  required={false}
                                />
                              }
                            />
                          </Div>
                        </Grid>
                      );
                    }
                    return (
                      <Grid
                        key={key}
                        item
                        xs={12}
                        md={isOberon ? 4 : 6}
                        lg={isOberon ? 4 : 6}
                      >
                        <Div sx={{ mt: 1, mb: 1 }}>
                          <TextField
                            fullWidth
                            size="medium"
                            type={cfg?.type || "text"}
                            label={cfg?.label || key}
                            helperText={cfg?.description || ""}
                            value={getAdditionalVariableValue(key)}
                            onChange={(e) =>
                              setAdditionalVariableValue(key, e.target.value)
                            }
                          />
                        </Div>
                      </Grid>
                    );
                  })}
                </>
              )}
              {isWooCommerce && (
                <>
                  <Grid item xs={12} md={6} lg={6}>
                    <Div sx={{ mt: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="medium"
                        type="text"
                        label="Consumer Key"
                        helperText="WooCommerce REST API kľúč (ck_...)"
                        value={getProductsCredentialValue("username")}
                        onChange={(e) =>
                          setProductsCredentialValue("username", e.target.value)
                        }
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <Div sx={{ mt: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="medium"
                        type="password"
                        autoComplete="new-password"
                        label="Consumer Secret"
                        placeholder={
                          wooHasStoredSecret ? "•••••••• (uložené)" : ""
                        }
                        helperText={
                          wooHasStoredSecret
                            ? "Uložené. Nechajte prázdne pre zachovanie, alebo zadajte nový secret."
                            : "WooCommerce REST API secret (cs_...)"
                        }
                        value={wooSecretInput}
                        onChange={(e) => {
                          setWooSecretInput(e.target.value);
                          setProductsCredentialValue(
                            "password",
                            e.target.value,
                          );
                        }}
                      />
                    </Div>
                  </Grid>
                  <Grid item xs={12} md={12} lg={12}>
                    <Div
                      sx={{
                        mt: 1,
                        mb: 1,
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                        alignItems: "center",
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ boxShadow: "none" }}
                        disabled={wooBusy || !customerId}
                        onClick={handleWooTest}
                      >
                        Otestovať spojenie
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ boxShadow: "none" }}
                        disabled={wooBusy || !customerId}
                        onClick={() => {
                          showDialog({
                            variant: "confirm",
                            title: t("widgets.confirmDialog.areYouSure"),
                            onYes: handleWooImport,
                            onNo: () => hideDialog(),
                          });
                        }}
                      >
                        Importovať produkty
                      </Button>
                      {wooBusy && (
                        <Typography variant="body2" color="text.secondary">
                          Pracujem…
                        </Typography>
                      )}
                    </Div>
                    {wooStatus?.summary && (
                      <Alert
                        variant="outlined"
                        severity={
                          wooStatus.running
                            ? "info"
                            : wooStatus.ok
                              ? "success"
                              : "error"
                        }
                        sx={{ mt: 1 }}
                      >
                        {wooStatus.summary}
                      </Alert>
                    )}
                  </Grid>
                </>
              )}
              {/* Force the DLA connection onto its own new row, below the 3-column settings. */}
              {oberonIsDla && <Grid item xs={12} md={12} lg={12} />}
              {oberonIsDla && (
                <Grid item xs={12} md={4} lg={4}>
                  <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
                    <JumboTextField
                      select
                      name="integrations.productsManagement.connectionCode"
                      label={t(
                        "pages.dsokDetails.integrations.oberon.connectionCode",
                        { defaultValue: "DLA (zákazník / kiosk)" },
                      )}
                    >
                      {dlaConnectionOptions.length === 0 && (
                        <MenuItem value="" disabled>
                          {t(
                            "pages.dsokDetails.integrations.oberon.noDlaAvailable",
                            { defaultValue: "Žiadna DLA nie je nakonfigurovaná" },
                          )}
                        </MenuItem>
                      )}
                      {dlaConnectionOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </JumboTextField>
                  </FormControl>
                </Grid>
              )}
              {oberonIsDla && (
                <Grid item xs={12} md={4} lg={4}>
                  <Div
                    sx={{
                      mt: 1,
                      mb: 1,
                      height: "100%",
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ boxShadow: "none" }}
                      disabled={
                        dlaImportBusy ||
                        !values?.integrations?.productsManagement?.connectionCode
                      }
                      onClick={() => runDlaImport("import")}
                    >
                      {t(
                        "pages.dsokDetails.integrations.oberon.fullImportViaDla",
                        { defaultValue: "Plný import cez DLA" },
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={
                        dlaImportBusy ||
                        !values?.integrations?.productsManagement?.connectionCode
                      }
                      onClick={() => runDlaImport("delta")}
                    >
                      {t("pages.dsokDetails.integrations.oberon.deltaViaDla", {
                        defaultValue: "Delta cez DLA",
                      })}
                    </Button>
                  </Div>
                </Grid>
              )}
              {showProductsFetchUrl && !oberonIsDla && (
                <Grid item xs={12} md={4} lg={4}>
                  <Div sx={{ mt: 1, mb: 1 }}>
                    <JumboTextField
                      fullWidth
                      required
                      name="integrations.productsManagement.api.fetchProductsUrl"
                      label={t("pages.dsokDetails.fetchProductsUrl")}
                    />
                  </Div>
                </Grid>
              )}
              {showImportProductsButton && !oberonIsDla && (
                <Grid item xs={4} md={4} lg={4}>
                  <Div sx={{ mt: 1, mb: 1, height: "100%" }}>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        boxShadow: "none",
                        height: "75%",
                      }}
                      disabled={
                        fetching ||
                        !values?.integrations?.productsManagement?.api
                          ?.fetchProductsUrl
                      }
                      onClick={() => {
                        showDialog({
                          variant: "confirm",
                          title: t("widgets.confirmDialog.areYouSure"),
                          onYes: () =>
                            fetchProducts(
                              values?.integrations?.productsManagement?.api
                                ?.fetchProductsUrl,
                            ),
                          onNo: () => hideDialog(),
                        });
                      }}
                    >
                      {t("pages.dsokDetails.fetchProducts")}
                    </Button>
                  </Div>
                </Grid>
              )}
            </Grid>
          </TabPanel>
          <TabPanel value={"externalOrders"} sx={{ p: 0 }}>
            <Grid item xs={12} md={12} lg={12} container spacing={2}>
              <Grid item xs={12} md={12} lg={12}>
                <Alert variant="outlined" severity="info">
                  {t("pages.dsokDetails.integrations.externalOrders.info")}
                </Alert>
              </Grid>

              <Grid item xs={12} md={3} lg={3}>
                <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Field
                        name="integrations.externalOrders.isActive"
                        component={Switch}
                        onChange={(e) =>
                          setFieldValue(
                            "integrations.externalOrders.isActive",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label={t(
                      "pages.dsokDetails.integrations.externalOrders.isActive",
                    )}
                    checked={
                      values?.integrations?.externalOrders?.isActive === true
                    }
                  />
                </Div>
              </Grid>

              <Grid item xs={12} md={3} lg={3}>
                {values?.integrations?.externalOrders?.isActive === true ? (
                  <FormControl sx={{ mt: 2, mb: 1 }} fullWidth>
                    <JumboTextField
                      select
                      InputLabelProps={{ required: false }}
                      name="integrations.externalOrders.source"
                      label={
                        <Label
                          text={t(
                            "pages.dsokDetails.integrations.externalOrders.source",
                          )}
                        />
                      }
                    >
                      <MenuItem value="none">
                        {t(
                          "pages.dsokDetails.integrations.externalOrders.sourceOptions.none",
                        )}
                      </MenuItem>
                      <MenuItem value="shopify">
                        {t(
                          "pages.dsokDetails.integrations.externalOrders.sourceOptions.shopify",
                        )}
                      </MenuItem>
                    </JumboTextField>
                  </FormControl>
                ) : null}
              </Grid>

              {values?.integrations?.externalOrders?.isActive === true &&
                values?.integrations?.externalOrders?.source === "shopify" && (
                  <>
                    <Grid item xs={12} md={12} lg={12}>
                      <Alert variant="outlined" severity="info">
                        {t(
                          "pages.dsokDetails.integrations.externalOrders.shopifySetupInfo",
                        )}
                      </Alert>
                    </Grid>

                    <Grid item xs={12} md={6} lg={6}>
                      <Div
                        sx={{
                          mt: 1,
                          mb: 1,
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                        }}
                      >
                        <TextField
                          fullWidth
                          label={t(
                            "pages.dsokDetails.integrations.externalOrders.shopifyApiUrl",
                          )}
                          value={shopifyApiUrl}
                          InputProps={{ readOnly: true }}
                        />
                        <Tooltip
                          title={t(
                            "pages.dsokDetails.integrations.externalOrders.copyApiUrl",
                            { defaultValue: "Copy" },
                          )}
                        >
                          <IconButton
                            aria-label={t(
                              "pages.dsokDetails.integrations.externalOrders.copyApiUrl",
                              { defaultValue: "Copy" },
                            )}
                            size="small"
                            onClick={handleCopyShopifyApiUrl}
                            disabled={!shopifyApiUrl}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Div>
                    </Grid>

                    <Grid item xs={12} md={6} lg={6}>
                      <Div sx={{ mt: 1, mb: 1 }}>
                        <JumboTextField
                          fullWidth
                          name="integrations.externalOrders.signature"
                          label={t(
                            "pages.dsokDetails.integrations.externalOrders.signature",
                          )}
                        />
                      </Div>
                    </Grid>

                    <Grid item xs={12} md={12} lg={12}>
                      <Alert variant="outlined" severity="info">
                        {t(
                          "pages.dsokDetails.integrations.externalOrders.ticketUrlHelp",
                        )}
                      </Alert>
                    </Grid>

                    <Grid item xs={12} md={12} lg={12}>
                      <Div
                        sx={{
                          mt: 1,
                          mb: 1,
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                        }}
                      >
                        <TextField
                          fullWidth
                          label={t(
                            "pages.dsokDetails.integrations.externalOrders.ticketUrl",
                            { defaultValue: "Ticket Url" },
                          )}
                          value={shopifyTicketUrlTemplate}
                          InputProps={{ readOnly: true }}
                        />
                        <Tooltip
                          title={t(
                            "pages.dsokDetails.integrations.externalOrders.copyTicketUrl",
                            { defaultValue: "Copy" },
                          )}
                        >
                          <IconButton
                            aria-label={t(
                              "pages.dsokDetails.integrations.externalOrders.copyTicketUrl",
                              { defaultValue: "Copy" },
                            )}
                            size="small"
                            onClick={handleCopyShopifyTicketUrl}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Div>
                    </Grid>

                    <Grid item xs={12} md={12} lg={12}>
                      <Div sx={{ mt: 1, mb: 1 }}>
                        <TextField
                          fullWidth
                          label={t(
                            "pages.dsokDetails.integrations.externalOrders.previewLabel",
                            { defaultValue: "Ukážka" },
                          )}
                          value={t(
                            "pages.dsokDetails.integrations.externalOrders.previewHint",
                            {
                              defaultValue:
                                "Click to open sample ticket page in a new tab",
                            },
                          )}
                          onClick={handleOpenProcessedOrderPreview}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleOpenProcessedOrderPreview();
                            }
                          }}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end">
                                <OpenInNewIcon
                                  fontSize="small"
                                  color="action"
                                  aria-hidden
                                />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiInputBase-input": {
                              cursor: "pointer",
                            },
                          }}
                          inputProps={{
                            readOnly: true,
                            tabIndex: 0,
                            role: "button",
                            "aria-label": t(
                              "pages.dsokDetails.integrations.externalOrders.previewAria",
                              {
                                defaultValue:
                                  "Open processed-order preview in a new tab",
                              },
                            ),
                          }}
                        />
                      </Div>
                    </Grid>
                  </>
                )}
            </Grid>
          </TabPanel>
        </TabContext>
      </Grid>
    </Grid>
  );

  if (hideSectionTitle) {
    return content;
  }

  return (
    <Accordion
      expanded={expanded === FIELD_NAME}
      onChange={handleChange(FIELD_NAME)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ width: "33%", flexShrink: 0 }}>
          {t("pages.dsokDetails.integrations.title")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{content}</AccordionDetails>
    </Accordion>
  );
};

export default SmartkioskIntegrationFields;
